import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { functions } from '../firebaseConfig';
import { httpsCallable } from "firebase/functions";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Mail, Clock, MapPin, Calendar, ChevronLeft, ChevronRight, Sparkles, Lock } from 'lucide-react';
import PageLayout from './layouts/PageLayout';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import debounce from 'lodash/debounce';
import { usePlan } from '../contexts/PlanContext';
import { useError } from '../contexts/ErrorContext';
import Button from '../components/ui/Button';

const GenerateMapPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [user, setUser] = useState(null);
  const auth = getAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRedo = searchParams.get('redo') === 'true';
  const { limits, usage, isAtLimit, hasSubscription, loading: planLoading } = usePlan();
  const { showError } = useError();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    data: '',
    hora: '',
    pais: '',
    estado: '',
    cidade: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
        
        // Fetch user data from Firestore
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setFormData(prevState => ({
              ...prevState,
              nome: userData.nomeCompleto || '',
              email: userData.email || currentUser.email
            }));
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setIsAuthenticated(false);
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const progressSteps = [
    { number: 1, label: 'Dados Pessoais', icon: <User size={20} /> },
    { number: 2, label: 'Local', icon: <MapPin size={20} /> },
    { number: 3, label: 'Confirmação', icon: <Sparkles size={20} /> }
  ];

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
  };

  const canAdvance = () => {
    if (currentStep === 1) {
      return formData.data && formData.hora;
    }
    if (currentStep === 2) {
      return !!selectedLocation;
    }
    return true;
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (!canAdvance()) return;
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const generateReport = httpsCallable(functions, 'create_astrological_report');
      await generateReport({
        name: formData.nome,
        year: formData.data.split("-")[0],
        month: formData.data.split("-")[1],
        day: formData.data.split("-")[2],
        hour: formData.hora.split(":")[0],
        minute: formData.hora.split(":")[1],
        city: formData.cidade,
        country: formData.pais,
        uid: user.uid,
        email: user.email
      });
      navigate('/meus-mapas', { state: { justGenerated: true } });
    } catch (error) {
      console.error('Error generating report:', error);
      showError(error);
      setIsLoading(false);
    }
  };

  const searchLocation = useMemo(() => debounce(async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const searchLocationFn = httpsCallable(functions, 'search_location');
      const result = await searchLocationFn({ query });
      setSearchResults(result.data);
    } catch (error) {
      console.error('Error searching location:', error);
    } finally {
      setIsSearching(false);
    }
  }, 400), []);
  
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setSearchResults([]);
    setFormData(prev => ({
      ...prev,
      cidade: location.name,
      estado: location.state || '',
      pais: location.country,
      latitude: location.lat,
      longitude: location.lon
    }));
  };

  const inputClasses = "w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700";
  const labelClasses = "block text-sm font-medium text-gray-700 mb-2";

  const renderPersonalInfo = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        Dados Pessoais
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className={labelClasses} htmlFor="nome">Nome Completo</label>
          <div className="relative">
            <User className="w-5 h-5 text-purple-400 absolute left-3 top-2.5" />
            <input
              type="text"
              id="nome"
              value={formData.nome}
              className={`${inputClasses} bg-gray-50 cursor-not-allowed`}
              placeholder="Seu nome completo"
              required
              readOnly
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className={labelClasses} htmlFor="email">E-mail</label>
          <div className="relative">
            <Mail className="w-5 h-5 text-purple-400 absolute left-3 top-2.5" />
            <input
              type="email"
              id="email"
              value={formData.email}
              className={`${inputClasses} bg-gray-50 cursor-not-allowed`}
              placeholder="seu@email.com"
              required
              readOnly
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className={labelClasses} htmlFor="data">Data de Nascimento</label>
          <div className="relative">
            <Calendar className="w-5 h-5 text-purple-400 absolute left-3 top-2.5" />
            <input
              type="date"
              id="data"
              value={formData.data}
              onChange={handleInputChange}
              className={inputClasses}
              required
            />
          </div>
          <p className="text-sm text-gray-500">Importante: use a data exata do nascimento</p>
        </div>

        <div className="space-y-2">
          <label className={labelClasses} htmlFor="hora">Hora do Nascimento</label>
          <div className="relative">
            <Clock className="w-5 h-5 text-purple-400 absolute left-3 top-2.5" />
            <input
              type="time"
              id="hora"
              value={formData.hora}
              onChange={handleInputChange}
              className={inputClasses}
              required
            />
          </div>
          <p className="text-sm text-gray-500">A hora influencia diretamente seu ascendente</p>
        </div>
      </div>
    </motion.div>
  );

  const renderLocationInfo = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        Local de Nascimento
      </h2>
      <div className="space-y-2">
        <label className={labelClasses} htmlFor="location">Buscar Local</label>
        <div className="relative">
          <MapPin className="w-5 h-5 text-purple-400 absolute left-3 top-2.5" />
          <input
            type="text"
            id="location"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchLocation(e.target.value);
            }}
            className={inputClasses}
            placeholder="Digite o nome da cidade..."
            autoComplete="off"
          />
          {isSearching && (
            <div className="absolute right-3 top-2.5">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
            </div>
          )}
          
          {searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-purple-100 max-h-60 overflow-auto">
              {searchResults.map((location, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleLocationSelect(location)}
                  className="w-full px-4 py-2 text-left hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                >
                  <div className="font-medium">{location.name}</div>
                  <div className="text-sm text-gray-500">
                    {[location.state, location.country].filter(Boolean).join(', ')}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {selectedLocation && (
          <div className="mt-4 p-4 bg-purple-50 rounded-lg">
            <h3 className="font-medium text-purple-600 mb-2">Local selecionado:</h3>
            <p className="text-gray-700">{selectedLocation.name}</p>
            <p className="text-gray-500 text-sm">
              {[selectedLocation.state, selectedLocation.country].filter(Boolean).join(', ')}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderConfirmation = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
        Confirmação dos Dados
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-purple-50 rounded-lg p-6 space-y-4">
          <h3 className="font-semibold text-purple-600">Dados Pessoais</h3>
          <div className="space-y-2 text-gray-700">
            <p className="flex items-center gap-2">
              <User size={16} />
              <span className="font-medium">Nome:</span> {formData.nome}
            </p>
            <p className="flex items-center gap-2">
              <Mail size={16} />
              <span className="font-medium">Email:</span> {formData.email}
            </p>
            <p className="flex items-center gap-2">
              <Calendar size={16} />
              <span className="font-medium">Data:</span> {formData.data}
            </p>
            <p className="flex items-center gap-2">
              <Clock size={16} />
              <span className="font-medium">Hora:</span> {formData.hora}
            </p>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-6 space-y-4">
          <h3 className="font-semibold text-purple-600">Local de Nascimento</h3>
          <div className="space-y-2 text-gray-700">
            <p className="flex items-center gap-2">
              <MapPin size={16} />
              <span className="font-medium">País:</span> {formData.pais}
            </p>
            <p className="flex items-center gap-2">
              <MapPin size={16} />
              <span className="font-medium">Estado:</span> {formData.estado}
            </p>
            <p className="flex items-center gap-2">
              <MapPin size={16} />
              <span className="font-medium">Cidade:</span> {formData.cidade}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );

  // Subscription gate: require Stripe checkout before generating maps
  if (!planLoading && !hasSubscription) {
    return (
      <PageLayout title={isRedo ? "Refazer Mapa Astral" : "Gerar Mapa Astral"} maxWidth="4xl">
        <div className="max-w-lg mx-auto py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 border border-purple-100 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-5">
              <Sparkles className="w-8 h-8 text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Escolha um plano para começar
            </h2>
            <p className="text-gray-500 mb-6">
              Para gerar seu mapa astral, primeiro escolha um dos nossos planos — incluindo o plano gratuito.
            </p>
            <Button onClick={() => navigate('/pricing')} fullWidth>
              Escolher Plano
            </Button>
          </motion.div>
        </div>
      </PageLayout>
    );
  }

  // Plan gate: block if map limit reached
  if (!planLoading && isAtLimit('own_maps')) {
    return (
      <PageLayout title={isRedo ? "Refazer Mapa Astral" : "Gerar Mapa Astral"} maxWidth="4xl">
        <div className="max-w-lg mx-auto py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 border border-purple-100 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-5">
              <Lock className="w-8 h-8 text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Limite de mapas atingido
            </h2>
            <p className="text-gray-500 mb-2">
              Voc&#234; j&#225; gerou {usage.own_maps} de {limits.own_maps === Infinity ? 'ilimitados' : limits.own_maps} mapa{limits.own_maps !== 1 ? 's' : ''} no plano atual.
            </p>
            <p className="text-gray-400 text-sm mb-6">
              Fa&#231;a upgrade para gerar mapas ilimitados e acessar a an&#225;lise completa.
            </p>
            <Button onClick={() => navigate('/pricing')} fullWidth>
              Ver Planos
            </Button>
          </motion.div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={isRedo ? "Refazer Mapa Astral" : "Gerar Mapa Astral"} maxWidth="4xl">
      <div className="max-w-4xl mx-auto">
        {/* New Progress Steps Design */}
        <div className="mb-12">
          <div className="flex justify-between items-center relative">
            {/* Progress Line */}
            <div className="absolute left-0 right-0 h-1 bg-gradient-to-r from-purple-100 to-pink-100 top-[2.125rem] transform">
              <div
                className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-500 ease-in-out"
                style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
              ></div>
            </div>

            {progressSteps.map((step) => (
              <div
                key={step.number}
                className="relative flex flex-col items-center group"
              >
                <div
                  className={`
                    w-16 h-16 rounded-full flex items-center justify-center z-10
                    transition-all duration-300 ease-in-out transform
                    ${step.number === currentStep 
                      ? 'scale-110 bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg shadow-purple-200' 
                      : step.number < currentStep
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600'
                        : 'bg-white border-2 border-purple-200'
                    }
                    ${step.number <= currentStep ? 'text-white' : 'text-purple-300'}
                  `}
                >
                  <motion.div
                    initial={false}
                    animate={{
                      scale: step.number === currentStep ? 1.2 : 1,
                      rotate: step.number === currentStep ? 360 : 0
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    {step.icon}
                  </motion.div>
                </div>
                
                <div className="absolute -bottom-8 w-max">
                  <span
                    className={`
                      text-sm font-medium transition-all duration-300
                      ${step.number === currentStep 
                        ? 'text-purple-600 font-semibold'
                        : step.number < currentStep
                          ? 'text-purple-600'
                          : 'text-gray-400'
                      }
                    `}
                  >
                    {step.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-purple-100 relative">
          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-2xl z-10 flex flex-col items-center justify-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-purple-500 animate-pulse" />
                </div>
                <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Gerando seu mapa astral...</h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">Calculando posições planetárias e preparando sua análise. Isso pode levar alguns segundos.</p>
            </div>
          )}

          <form onSubmit={currentStep === 3 ? handleSubmit : handleNextStep}>
            {currentStep === 1 && renderPersonalInfo()}
            {currentStep === 2 && renderLocationInfo()}
            {currentStep === 3 && renderConfirmation()}

            <div className="flex justify-between mt-8 pt-6 border-t border-purple-100">
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handlePrevStep}
                  disabled={isLoading}
                >
                  <ChevronLeft size={20} />
                  Voltar
                </Button>
              )}

              {currentStep < 3 && (
                <Button
                  type="submit"
                  disabled={!canAdvance()}
                  className="ml-auto"
                >
                  Continuar
                  <ChevronRight size={20} />
                </Button>
              )}

              {currentStep === 3 && (
                <Button
                  type="submit"
                  disabled={isLoading}
                  loading={isLoading}
                  loadingText="Gerando..."
                  className="ml-auto"
                >
                  Gerar Mapa Astral
                  <Sparkles size={20} />
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </PageLayout>
  );
};

export default GenerateMapPage;